import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';

import {
  IconChevronDown,
  IconFilter,
  IconInfoCircle,
  IconFilePlus,
} from '@tabler/icons';

import api from '../../../services/ApiManagement/Index';
import { useAuth } from '../../../hooks/auth';
import { useOnlineStatus } from '../../../hooks/onlineStatus';

import BackHeader from '../../../components/BackHeader';
import AnimalItem from '../../../components/Animal/Item';
import RegisterItem from '../../../components/RegisterItem';
import { Skeleton, Label } from '../../../components/Generic';
import {
  Container,
  Content,
  SelectContainer,
  Select,
  SelectIconWrapper,
  NotFoundContainer,
  IconContainer,
} from './styles';

const Registers: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [animal, setAnimal] = useState<any>({});
  const [registers, setRegisters] = useState<any>([]);
  const [canEdit, setCanEdit] = useState(false);

  const { id_property, id_animal }: any = useParams();
  const history = useHistory();
  const isOnline = useOnlineStatus();
  const { user } = useAuth();

  useEffect(() => {
    getAnimal();
    getRegisters('');
  }, []);

  const getAnimal = async () => {
    try {
      const animalResponse = await api.get(
        `/animais/${id_animal}`,
        isOnline,
        'animal',
        {
          headers: {
            propriedade_id: id_property,
          },
        },
      );

      setAnimal(animalResponse.data);
      //console.log(animalResponse.data);
    } catch (err) {
      console.log(err);
    }
  };

  const getRegisters = async (type: string) => {
    try {
      setLoading(true);

      const registersResponse = await api.get(
        `/registros/todos/${id_animal}`,
        isOnline,
        'registros',
        {
          headers: {
            id: id_animal,
            propriedade_id: id_property,
          },
          params: {
            keyword: type,
            pagina: 1,
            limite: 999,
          },
        },
      );

      setRegisters(registersResponse.data);
      console.log(registersResponse.data);

      // Propriedade
      const propertyResponse = await api.get(
        `/propriedades/`,
        isOnline,
        'propriedades',
        {
          headers: {
            propriedade_id: id_property,
          },
        },
      );

      if (propertyResponse.data.proprietario_id === user.id) {
        setCanEdit(true);
      } else {
        // Faz parte?
        const userExists = propertyResponse.data.colaboradores.some(
          (el: any) => {
            return el.usuario_id === user.id;
          },
        );
        setCanEdit(userExists);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const selectOptions =
    animal.sexo === 'femea'
      ? [
          { value: '', label: 'Nenhum' },
          { value: 'peso', label: 'Peso' },
          { value: 'vacinacao', label: 'Vacinação' },
          { value: 'inseminacao', label: 'Inseminação' },
          {
            value: 'diagnostico_de_gestacao',
            label: 'Diagnóstico de gestação',
          },
          { value: 'paricao', label: 'Parição' },
          { value: 'observacao', label: 'Observação' },
        ]
      : [
          { value: '', label: 'Nenhum' },
          { value: 'peso', label: 'Peso' },
          { value: 'vacinacao', label: 'Vacinação' },
          { value: 'perimetro_escrotal', label: 'Perímetro escrotal' },
          { value: 'observacao', label: 'Observação' },
        ];

  return (
    <Container>
      <BackHeader title="Registros">
        {canEdit && (
          <IconContainer
            onClick={() =>
              history.push(
                `/propriedade/${id_property}/animais/${id_animal}/registros/novo`,
              )
            }
            id="edit-button"
          >
            <IconFilePlus size={24} />
          </IconContainer>
        )}
      </BackHeader>

      <Content>
        <AnimalItem
          gender={animal.sexo}
          name={animal.nome}
          number={animal.numero_de_identificacao}
          birth={animal.data_de_nascimento}
          imageUrl={animal.imagens?.[0]?.image_url}
          state={animal.estado}
          belongs={animal.pertencente}
          customStyle={{ marginBottom: 30 }}
        />

        <Label style={{ marginBottom: 10 }}>Filtros</Label>

        <SelectContainer>
          <SelectIconWrapper style={{ left: 20 }}>
            <IconFilter size={20} />
          </SelectIconWrapper>

          <Select onChange={e => getRegisters(e.target.value)}>
            {selectOptions.map(option => (
              <option value={option.value} key={option.value}>
                {option.label}
              </option>
            ))}
          </Select>

          <SelectIconWrapper style={{ right: 20 }}>
            <IconChevronDown size={20} />
          </SelectIconWrapper>
        </SelectContainer>

        {loading ? (
          <LoadingComponent />
        ) : registers?.length > 0 ? (
          registers?.map((register: any) => (
            <RegisterItem
              type={register.categoria}
              date={register.data}
              description={register.animalRegistro?.nome || register.descricao}
              category={register.rotulo}
              canEdit={canEdit}
              onClick={() =>
                canEdit
                  ? history.push(
                      `/propriedade/${id_property}/animais/${id_animal}/registros/${register.id}/editar`,
                    )
                  : null
              }
            />
          ))
        ) : (
          <NotFoundContainer>
            <IconInfoCircle size={24} />

            <strong>Nenhum registro encontrado.</strong>
          </NotFoundContainer>
        )}
      </Content>
    </Container>
  );
};

const LoadingComponent = () => (
  <>
    <Skeleton
      style={{
        marginBottom: 20,
        height: 42,
        width: '100%',
        borderRadius: 8,
      }}
    />
    <Skeleton
      style={{
        marginBottom: 20,
        height: 42,
        width: '100%',
        borderRadius: 8,
      }}
    />
    <Skeleton
      style={{
        marginBottom: 20,
        height: 42,
        width: '100%',
        borderRadius: 8,
      }}
    />
    <Skeleton
      style={{
        marginBottom: 20,
        height: 42,
        width: '100%',
        borderRadius: 8,
      }}
    />
  </>
);

export default Registers;
